import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Activity, 
  Loader2,
  Search,
  Phone,
  User,
  CalendarDays,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAvailabilityActivity = () => {
  const [activity, setActivity] = useState({ leads: [], total_checks: 0, total_leads: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchActivity();
  }, [period, resultFilter]);

  const fetchActivity = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const params = new URLSearchParams();
      if (period) params.append("period", period);
      if (resultFilter) params.append("result_filter", resultFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/availability-activity?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivity(response.data);
    } catch (error) {
      console.error("Failed to fetch activity:", error);
      toast.error("Failed to load activity data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchActivity();
  };

  const handleViewDetail = async (lead) => {
    setSelectedLead(lead);
    setLoadingDetail(true);
    
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/availability-activity/${lead.lead_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadDetail(response.data);
    } catch (error) {
      toast.error("Failed to load lead details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", { 
      day: "numeric", 
      month: "short", 
      year: "numeric" 
    });
  };

  const formatDateTime = (isoStr) => {
    const date = new Date(isoStr);
    return date.toLocaleString("en-IN", { 
      day: "numeric", 
      month: "short", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const maskPhone = (phone) => {
    if (!phone || phone.length < 10) return phone;
    return phone.slice(0, 2) + "****" + phone.slice(-4);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#B08968]" />
              Availability Activity
            </h1>
            <p className="text-gray-600 mt-1">
              Track customer availability checks and inquiries
            </p>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="bg-blue-50 px-3 py-1.5 rounded-lg">
              <span className="text-blue-600 font-medium">{activity.total_leads}</span>
              <span className="text-blue-500 ml-1">leads</span>
            </div>
            <div className="bg-purple-50 px-3 py-1.5 rounded-lg">
              <span className="text-purple-600 font-medium">{activity.total_checks}</span>
              <span className="text-purple-500 ml-1">checks</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Filters:</span>
            </div>
            
            {/* Search */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search name or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-48"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button variant="outline" size="sm" onClick={handleSearch}>
                <Search className="w-4 h-4" />
              </Button>
            </div>

            {/* Period Filter */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Time</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>

            {/* Result Filter */}
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Results</option>
              <option value="AVAILABLE">Available</option>
              <option value="NOT_AVAILABLE">Not Available</option>
            </select>
          </div>
        </div>

        {/* Activity Table */}
        {activity.leads.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
            <p className="text-gray-500">
              Customer availability checks will appear here.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Customer</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Phone</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Last Check</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Result</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Attempts</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Last Active</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {activity.leads.map((lead) => {
                    const lastCheck = lead.checks[0];
                    return (
                      <tr key={lead.lead_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#B08968]/10 flex items-center justify-center">
                              <User className="w-4 h-4 text-[#B08968]" />
                            </div>
                            <span className="font-medium text-gray-900">{lead.customer_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.phone_number}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {lastCheck && (
                            <div className="text-sm">
                              <div className="flex items-center gap-1.5 text-gray-900">
                                <CalendarDays className="w-3.5 h-3.5" />
                                {formatDate(lastCheck.check_in)} → {formatDate(lastCheck.check_out)}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            lead.last_result === "AVAILABLE" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}>
                            {lead.last_result === "AVAILABLE" ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            {lead.last_result === "AVAILABLE" ? "Available" : "Blocked"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{lead.total_attempts}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDateTime(lead.last_activity)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(lead)}
                            className="h-8"
                          >
                            View <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Lead Detail Modal */}
        <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lead Activity Details</DialogTitle>
            </DialogHeader>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
              </div>
            ) : leadDetail ? (
              <div className="space-y-6 mt-4">
                {/* Lead Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Customer Name</p>
                      <p className="font-medium">{leadDetail.lead.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{leadDetail.lead.phone_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">First Seen</p>
                      <p className="font-medium">{formatDateTime(leadDetail.first_seen)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Last Active</p>
                      <p className="font-medium">{formatDateTime(leadDetail.last_seen)}</p>
                    </div>
                  </div>
                </div>

                {/* Availability Checks */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Availability Checks ({leadDetail.total_checks})
                  </h4>
                  <div className="space-y-2">
                    {leadDetail.checks.map((check) => (
                      <div 
                        key={check.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          check.availability_result === "AVAILABLE" 
                            ? "bg-green-50 border-green-200" 
                            : "bg-red-50 border-red-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {check.availability_result === "AVAILABLE" ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium">
                              {formatDate(check.check_in)} → {formatDate(check.check_out)}
                            </p>
                            <p className="text-xs text-gray-500">
                              Checked at {formatDateTime(check.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${
                          check.availability_result === "AVAILABLE" 
                            ? "text-green-700" 
                            : "text-red-700"
                        }`}>
                          {check.availability_result === "AVAILABLE" ? "Available" : "Not Available"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity Timeline */}
                {leadDetail.events && leadDetail.events.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Activity Timeline
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {leadDetail.events.map((event) => (
                        <div 
                          key={event.id} 
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm"
                        >
                          <div className="w-2 h-2 rounded-full bg-[#B08968]" />
                          <span className="font-medium text-gray-700">{event.event_name}</span>
                          <span className="text-gray-400 text-xs ml-auto">
                            {formatDateTime(event.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAvailabilityActivity;
